import { Add, Delete, Edit, Save, SearchOff } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditOffIcon from "@mui/icons-material/EditOff";
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRowId,
  GridRowModes,
  GridRowModesModel
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReferenceData } from "../../../hooks";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { LithologyForm as LithologyFormType } from "../../../types/forms/capture";
import { CaptureLithologyResponse } from "../../../types/model/captureLithology";
import { LithologiesImportDialog } from "../../Imports/Lithologies/LithologiesImportDialog";

interface CaptureLithologiesDataGridProps {
  captureId: number;
  canEdit: boolean;
  lithologiesData: CaptureLithologyResponse[];
  onCreate: (values: LithologyFormType) => Promise<void>;
  onUpdate: (id: number, values: LithologyFormType) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onRequestRefresh?: () => void;
}
export type LithologyGridRow =
  | CaptureLithologyResponse
  | {
      id: string;
      lithologyId: number;
      startDepth: number;
      endDepth: number;
      hydraulicConductivity: number | null;
      isNew: boolean;
    };

export default function CaptureLithologiesDataGrid({
  captureId,
  canEdit,
  lithologiesData,
  onCreate,
  onUpdate,
  onDelete,
  onRequestRefresh
}: Readonly<CaptureLithologiesDataGridProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const formStyles = useFormStyles(true);
  const [importOpen, setImportOpen] = useState(false);

  const [rows, setRows] = useState<LithologyGridRow[]>([]);
  const [rowModesModel, setRowModesModel] =
    useState<GridRowModesModel>({});
  const { data: lithologies = [] } = useReferenceData("geology", "lithologies");

  useEffect(() => {
    setRows(lithologiesData);
  }, [lithologiesData]);

  const handleAdd = () => {
    if (rows.some((r) => "isNew" in r && r.isNew)) return;

    const tempId = `new-${Date.now()}`;

    setRows((old) => [
      {
        id: tempId,
        lithologyId: null as unknown as number,
        startDepth: null as unknown as number,
        endDepth: null as unknown as number,
        hydraulicConductivity: null,
        isNew: true,
      },
      ...old,
    ]);

    setRowModesModel((old) => ({
      ...old,
      [tempId]: { mode: GridRowModes.Edit },
    }));
  };

  const handleCancel = (id: GridRowId) => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: {
        mode: GridRowModes.View,
        ignoreModifications: true,
      },
    }));

    setRows((prevRows) =>
      prevRows.filter(
        (row) => row.id !== id || !("isNew" in row && row.isNew)
      )
    );
  };

  const processRowUpdate = async (
    newRow: LithologyGridRow
  ): Promise<LithologyGridRow> => {

    if (!newRow.lithologyId) {
      throw new Error(
        t("captures.capture.lithology.lithology-required")
      );
    }

    if (newRow.startDepth >= newRow.endDepth) {
      throw new Error(
        t("captures.capture.lithology.invalid-depth")
      );
    }

    const payload: LithologyFormType = {
      lithologyId: newRow.lithologyId,
      startDepth: newRow.startDepth,
      endDepth: newRow.endDepth,
      hydraulicConductivity: newRow.hydraulicConductivity,
    };

    if ("isNew" in newRow && newRow.isNew) {
      await onCreate(payload);
      return { ...newRow, isNew: false };
    }

    await onUpdate(newRow.id as number, payload);
    return newRow;
  };

  const baseColumns: GridColDef[] = [
    {
      field: "lithologyId",
      headerName: t("captures.capture.lithology.lithology-type"),
      flex: 1,
      editable: true,

      valueFormatter: (value) =>
        lithologies.find((l) => l.id === value)?.name ?? "-",

      renderEditCell: (params) => {
        const { id, value, api } = params;

        return (
          <Autocomplete
            fullWidth
            sx={{ ...formStyles }}
            options={lithologies}
            getOptionLabel={(option) => option.name}
            value={
              lithologies.find((l) => l.id === value) ?? null
            }
            onChange={(_, newValue) => {
              api.setEditCellValue({
                id,
                field: "lithologyId",
                value: newValue?.id ?? null,
              });
            }}
            renderInput={(paramsInput) => (
              <TextField
                {...paramsInput}
                required
                error={!value}
              />
            )}
          />
        );
      },
    },
    {
      field: "startDepth",
      headerName: t("captures.capture.lithology.start-depth"),
      flex: 1,
      editable: true,
      renderCell: (params) =>
        params.value == null ? "-" : `${params.value} ${t("generic.units.m")}`,
      renderEditCell: (params) => {
        const { id, value, api } = params;

        return (
          <TextField
            fullWidth
            sx={{ ...formStyles }}
            type="number"
            required
            value={value ?? ""}
            onChange={(e) => {
              const newValue =
                e.target.value === ""
                  ? null
                  : Number(e.target.value);

              api.setEditCellValue({
                id,
                field: "startDepth",
                value: newValue,
              });
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                ),
              },
            }}
            error={value === null}
          />
        );
      },
    },
    {
      field: "endDepth",
      headerName: t("captures.capture.lithology.end-depth"),
      flex: 1,
      editable: true,
      renderCell: (params) =>
        params.value == null ? "-" : `${params.value} ${t("generic.units.m")}`,
      renderEditCell: (params) => {
        const { id, value, api } = params;

        return (
          <TextField
            fullWidth
            sx={{ ...formStyles }}
            type="number"
            required
            value={value ?? ""}
            onChange={(e) => {
              const newValue =
                e.target.value === ""
                  ? null
                  : Number(e.target.value);

              api.setEditCellValue({
                id,
                field: "endDepth",
                value: newValue,
              });
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                ),
              },
            }}
            error={value === null}
          />
        );
      },
    },
    {
      field: "thickness",
      headerName: t("captures.capture.lithology.thickness"),
      flex: 1,
      renderHeader: (params) => (
        <Box sx={{ whiteSpace: "normal", lineHeight: 1.2, fontWeight: 500 }}>
          {params.colDef.headerName}
        </Box>
      ),
      valueGetter: (_, row) =>
        row.endDepth != null && row.startDepth != null
          ? row.endDepth - row.startDepth
          : "-",
      renderCell: (params) =>
        params.value == null ? "-" : `${params.value} ${t("generic.units.m")}`,
    },
    {
      field: "hydraulicConductivity",
      headerName: t("captures.capture.lithology.hydraulic-conductivity"),
      flex: 1,
      editable: true,
      renderHeader: (params) => (
        <Box sx={{ whiteSpace: "normal", lineHeight: 1.2, fontWeight: 500 }}>
          {params.colDef.headerName}
        </Box>
      ),
      renderCell: (params) =>
        params.value == null ? "-" : `${params.value} ${t("generic.units.m-per-day")}`,
      renderEditCell: (params) => {
        const { id, value, api } = params;

        return (
          <TextField
            fullWidth
            sx={{ ...formStyles }}
            type="number"
            value={value ?? ""}
            onChange={(e) => {
              const newValue =
                e.target.value === ""
                  ? null
                  : Number(e.target.value);

              api.setEditCellValue({
                id,
                field: "hydraulicConductivity",
                value: newValue,
              });
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">{t("generic.units.m-per-day")}</InputAdornment>
                ),
              },
            }}
          />
        );
      },
    },
  ];

  const actionsColumn: GridColDef = {
    field: "actions",
    type: "actions",
    width: 120,
    headerName: t("generic.actions"),
    renderHeader: (params) => (
      <Box sx={{ whiteSpace: "normal", lineHeight: 1.2, fontWeight: 500 }}>
        {params.colDef.headerName}
      </Box>
    ),
    getActions: ({ id }) => {
      const isInEditMode =
        rowModesModel[id]?.mode === GridRowModes.Edit;

      if (isInEditMode) {
        return [
          <Tooltip key="cancel" title={t("generic.cancel")}>
            <GridActionsCellItem
              icon={<EditOffIcon />}
              label={t("generic.cancel")}
              color="error"
              onClick={() => handleCancel(id)}
            />
          </Tooltip>,
          <Tooltip key="save" title={t("generic.save")}>
            <GridActionsCellItem
              icon={<Save />}
              label={t("generic.save")}
              color="success"
              onClick={() =>
                setRowModesModel({
                  ...rowModesModel,
                  [id]: { mode: GridRowModes.View },
                })
              }
            />
          </Tooltip>,
        ];
      }

      return [
        ...(canEdit
          ? [
              <Tooltip key="edit" title={t("generic.edit")}>
                <GridActionsCellItem
                  icon={<Edit />}
                  label={t("generic.edit")}
                  color="primary"
                  onClick={() =>
                    setRowModesModel({
                      ...rowModesModel,
                      [id]: { mode: GridRowModes.Edit },
                    })
                  }
                />
              </Tooltip>,
            ]
          : []),

        ...(canEdit
          ? [
              <Tooltip key="delete" title={t("generic.delete")}>
                <GridActionsCellItem
                  icon={<Delete />}
                  label={t("generic.delete")}
                  color="error"
                  onClick={() => onDelete(id as number)}
                />
              </Tooltip>,
            ]
          : []),
      ];
    },
  };

  const columns: GridColDef[] = (canEdit)
    ? [...baseColumns, actionsColumn]
    : baseColumns;

  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        mb={2}
      >
        <Typography variant="h6" fontWeight={700}>
          {t("captures.capture.lithology.title")}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {canEdit && (
            <>
              <Tooltip title={t("generic.add")}>
                <IconButton onClick={handleAdd}>
                  <Add />
                </IconButton>
              </Tooltip>
            
              <Tooltip title={t("imports.import-dialog.title")}>
                <IconButton onClick={() => setImportOpen(true)}>
                  <CloudUploadIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Stack>

      <Box sx={{ height: 420, position: 'relative' }}>
        <DataGrid<LithologyGridRow>
          rows={rows}
          columns={columns}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={setRowModesModel}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(error) =>
            handleError(error)
          }
          getRowHeight={(params) =>
            ("isNew" in params.model && params.model.isNew) ||
            rowModesModel[params.id]?.mode === GridRowModes.Edit
              ? 58
              : 52
          }
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 25]}
          getRowId={(row) => row.id}
          sx={{
            "& .MuiDataGrid-overlay": {
              display: "none",
            },
          }}
        />

        {rows.length === 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0.6,
              pointerEvents: 'none',
            }}
          >
            <SearchOff sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6">
              {t("generic.no-records")}
            </Typography>
          </Box>
        )}
      </Box>

      <LithologiesImportDialog
        captureId={captureId}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={() => {
          setImportOpen(false);
          onRequestRefresh?.();
        }}
      />
    </Paper>
  );
}